import { useState, Fragment, useEffect, useContext, useMemo } from 'react'
import DateInput from '../DateInput/DateInput';
import DocumentButton from '../DocumentButton/DocumentButton';
import Input from '../Input/Input';
import InputDropDown from '../InputDropDown/InputDropDown';
import Modal from '../Modal/Modal';
import Stepper from '../Stepper/Stepper';
import moment, { Moment } from 'moment';
import ApiService from '../../services/ApiService';
import { IClinic, IFile, IPatient, IPriority, IReferrer, IReportType, ITextInput } from '../../helpers/Interfaces';
import { createMediaDocument, removeMedia } from '../../helpers/FirebaseFunctions';
import { formatLocalISO } from '../../helpers/HelperFunctions';
import FileUploadUI from '../FileUploadUI/FileUploadUI';
import BlueHomeDot from "../../assets/images/BlueHomeDot.svg";
import BlueHomeDotDisable from "../../assets/images/BlueHomeDotDisable.svg";
import { AppointmentReport } from '../../store/Store';
import CheckBox from '../CheckBox/CheckBox';

import "./AddReport.scss"

type AddReportForm = {
    patientName: string,
    patientNameId: string,
    dueDate: Moment,
    priorityId: number | null,
    clinicId: string,
    reportTypeId: string,
    providerNumber: string | null,
    placeOfAssessment: string | null,
    dateOfAssessment: Moment,
    referrerId: string | null,
    reportName: string | null,
    notes: string | null,
    mediaId: string,
    totalDuration: number,
    adHoc: boolean,
    associateAppointmentId: string | null | undefined,
    paperworkReceived: boolean | null,
    invoiceStatus: number | null,
    referrer: string | null
}

export default function AddReport(props: { open: boolean, close: CallableFunction }) {

    const apiService = new ApiService();
    const { changeAppointmentReport, clinicName, patientName, dateOfAssessment,
        appointmentId, notes, paperworkReceived, invoiceStatus, referrerId } = useContext(AppointmentReport);

    const [view, setView] = useState(0);
    const [formData, setFormData] = useState<AddReportForm>({
        patientName: "", patientNameId: "", dueDate: moment().utc().local(), priorityId: null, clinicId: "", reportTypeId: "",
        providerNumber: "", placeOfAssessment: "", dateOfAssessment: moment().utc().local(), referrerId: null, reportName: null,
        notes: "", mediaId: new Date().getTime().toString(), totalDuration: 0, adHoc: false,
        associateAppointmentId: appointmentId, paperworkReceived: false, invoiceStatus: 0, referrer: null
    });
    const [formDataErrors, setFormDataErrors] = useState({
        patientNameError: { showError: false, message: "" }, dueDateError: { showError: false, message: "" },
        priorityError: { showError: false, message: "" }, reportTypeIdError: { showError: false, message: "" },
        providerNumberError: { showError: false, message: "" }, placeOfAssessmentError: { showError: false, message: "" },
        dateOfAssessmentError: { showError: false, message: "" }
    });
    const [media, setMedia] = useState([] as IFile[]);
    const [loading, setLoading] = useState(false);
    const [showAlert, setShowAlert] = useState({ isError: false, message: "", open: false });
    const [clinics, setClinics] = useState([] as ITextInput[]);
    const [reportTypes, setReportTypes] = useState([] as ITextInput[]);
    const [priorities, setPriorities] = useState([] as ITextInput[]);
    const [patients, setPatients] = useState([] as ITextInput[]);
    const [referrers, setReferrers] = useState([] as ITextInput[]);
    const [tempPriorities, setTempPriorities] = useState([] as IPriority[]);
    const [addedFiles, setAddedFiles] = useState([] as IFile[]);

    const invoiceStatusValues: ITextInput[] = [
        { label: "Not Created", value: 0 },
        { label: "Invoice Created", value: 1 },
        { label: "Invoice Sent", value: 2 }
    ]

    useEffect(() => {
        let newForm = formData;
        newForm.patientName = patientName;
        newForm.dateOfAssessment = moment(dateOfAssessment).startOf('day');

        if (notes) {
            newForm.notes = notes
        }

        if (clinics.length !== 0) {
            let clinic = clinics.find((clinic) => clinic.label === clinicName);

            if (clinic) {
                newForm.clinicId = clinic?.value.toString();
            }
        }

        if (paperworkReceived !== null) {
            newForm.paperworkReceived = paperworkReceived;
        }

        if (invoiceStatus !== null) {
            newForm.invoiceStatus = invoiceStatus;
        }

        if (referrers.length !== 0) {
            let referrer = referrers.find((referrer) => referrer.value === referrerId);

            if (referrer) {
                newForm.referrerId = referrer?.value.toString();
                newForm.referrer = referrer?.label.toString();
            }
        }

        setFormData((prevDetails) => ({ ...prevDetails, ...newForm }));
    }, [patientName, clinics, clinicName, dateOfAssessment, notes, paperworkReceived, invoiceStatus, referrers, referrerId]);

    useEffect(() => {
        // getClinics();
        getReportTypes();
        getPriorities();
        getReferrers();
        getPatient();
    }, [])

    useEffect(() => {
        if (formData.priorityId !== null) {
            let numberOfDays = tempPriorities.filter((priority) => { return priority.id == formData.priorityId })[0]?.numberOfDaysAdded
            setFormData((prevDetails) => {
                const baseDate = prevDetails.dateOfAssessment;
                const newDueDate = baseDate.clone ? baseDate.clone().add(numberOfDays, 'day') : baseDate.add(numberOfDays, 'day');
                return { ...prevDetails, dueDate: newDueDate };
            });
        }
    }, [formData.priorityId, formData.dateOfAssessment])

    useEffect(() => {
        let duration = 0;

        media.forEach((file) => {
            duration = duration + file.duration
        });

        let form = formData;
        form.totalDuration = duration;

        if (duration > 0) {
            form.adHoc = false;
        }

        setFormData((previousData) => ({ ...previousData, ...form }));
    }, [media, media.length]);

    useEffect(() => {
        if (formData.clinicId !== "") {
            getReportTypes();
        }
    }, [formData.clinicId])

    const closeAlert = () => {
        let newShowAlert = showAlert;
        newShowAlert.open = false;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }

    const handleNext = () => {
        setView(view + 1);
    }

    const handlePrevious = () => {
        setView(view - 1);
    }

    function getReferrers() {

        setLoading(true);

        apiService.getReferrers().then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            getReferrersTextInput(data?.referrers);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function getPatient() {

        setLoading(true);

        apiService.getPatient().then((result) => {
            let data = result.data;
            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            getPatientTextInput(data.patients);

        }).catch((e) => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function getPatientTextInput(patients: IPatient[]) {

        setLoading(true);

        let patientArr = [] as ITextInput[];

        patients.forEach(patient => {
            let data = {
                label: patient.name,
                value: patient.id
            }

            patientArr.push(data);
        });

        setPatients(patientArr);

        setLoading(false);
    }

    function getReferrersTextInput(referrers: IReferrer[]) {

        setLoading(true);

        let referrersArr = [] as ITextInput[];

        referrers.forEach(referrer => {
            let data = {
                label: referrer.name,
                value: referrer.id
            }

            referrersArr.push(data);
        });

        setReferrers(referrersArr);

        setLoading(false);
    }

    function getPriorities() {

        setLoading(true);

        apiService.getPriorities().then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            setTempPriorities(data?.priorities);
            getPrioritiesTextInput(data?.priorities);

            let newFormData = formData;
            newFormData.priorityId = getHighestPriorityId(data?.priorities) as any;
            updateFormData(newFormData);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function getPrioritiesTextInput(priorities: IPriority[]) {

        setLoading(true);

        let prioritiesArr = [] as ITextInput[];

        priorities.forEach(priority => {
            let data = {
                label: priority.name,
                value: priority.id.toString()
            }

            prioritiesArr.push(data);
        });

        setPriorities(prioritiesArr);
        setLoading(false);
    }

    function getReportTypes() {

        setLoading(true);

        const clinicId = formData.clinicId !== "" ? formData.clinicId : null;

        let data = {
            clinicId: clinicId === undefined ? null : clinicId
        }

        apiService.getReportTypesByDoctorIdOrClinicId(data).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            getReportTypeTextInput(data?.reportTypes);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    function getClinics() {

        setLoading(true);

        let data = {
            doctorId: null
        }

        apiService.getClinicsByDoctorId(data).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            getClinicTextInput(data?.clinics);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    const clearData = () => {
        let newFormData = formData;
        newFormData.patientName = "";
        newFormData.dueDate = moment();
        newFormData.priorityId = null;
        newFormData.clinicId = "";
        newFormData.reportTypeId = "";
        newFormData.providerNumber = "";
        newFormData.placeOfAssessment = "";
        newFormData.dateOfAssessment = moment();
        newFormData.referrerId = null;
        newFormData.reportName = null;
        newFormData.notes = "";
        newFormData.mediaId = new Date().getTime().toString();
        newFormData.totalDuration = 0;
        newFormData.adHoc = false;
        newFormData.associateAppointmentId = null;
        setFormData((prevDetails: any) => ({ ...prevDetails, ...newFormData, }));

        let newFormDataErrors = formDataErrors;
        newFormDataErrors.patientNameError.showError = false;
        newFormDataErrors.dueDateError.showError = false;
        newFormDataErrors.priorityError.showError = false;
        newFormDataErrors.reportTypeIdError.showError = false;
        newFormDataErrors.providerNumberError.showError = false;
        newFormDataErrors.placeOfAssessmentError.showError = false;
        newFormDataErrors.dateOfAssessmentError.showError = false;
        setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));

        let newShowAlert = showAlert;
        newShowAlert.open = false;
        setShowAlert(prevDetails => ({ ...prevDetails, ...newShowAlert }));

        setMedia([]);
        setLoading(false);
        setView(0);
    }

    const handleClose = (shouldUpdate: boolean) => {
        clearData();
        changeAppointmentReport({
            clinicName: '', patientName: '', dateOfAssessment: null, appointmentId: null,
            invoiceStatus: null, referrerId: null, paperworkReceived: null, notes: ""
        })
        props.close("add", shouldUpdate);
    }

    const closeModal = (shouldUpdate: boolean) => {

        setLoading(true);

        changeAppointmentReport({
            clinicName: '', patientName: '', dateOfAssessment: null, appointmentId: null,
            invoiceStatus: null, referrerId: null, paperworkReceived: null, notes: ""
        })

        addedFiles.forEach((file) => {
            removeMedia(file.fileId);
            let _formData = formData;
            _formData.totalDuration = formData.totalDuration - file.duration;
            setFormData(_formData);
        });

        handleClose(shouldUpdate);
    }

    const updateFormData = (newFormData: any) => {
        setFormData((prevDetails) => ({ ...prevDetails, ...newFormData }));
    }

    function toMedia() {

        setLoading(true);

        if (formData.patientName?.trim() === "") {
            let newFormDataErrors = formDataErrors;
            newFormDataErrors.patientNameError.showError = true;
            newFormDataErrors.patientNameError.message = "REQUIRED";
            setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
            setLoading(false);
            return;
        } else {
            let newFormDataErrors = formDataErrors;
            newFormDataErrors.patientNameError.showError = false;
            setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
        }

        if (!(formData.dueDate instanceof moment)) {
            let newFormDataErrors = formDataErrors;
            newFormDataErrors.dueDateError.showError = true;
            newFormDataErrors.dueDateError.message = "REQUIRED";
            setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
            setLoading(false);
            return;
        } else {
            let newFormDataErrors = formDataErrors;
            newFormDataErrors.dueDateError.showError = false;
            setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
        }

        if (formData.priorityId === null) {
            let newFormDataErrors = formDataErrors;
            newFormDataErrors.priorityError.showError = true;
            newFormDataErrors.priorityError.message = "REQUIRED";
            setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
            setLoading(false);
            return;
        } else {
            let newFormDataErrors = formDataErrors;
            newFormDataErrors.priorityError.showError = false;
            setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
        }

        if (formData.reportTypeId?.trim() === "") {
            let newFormDataErrors = formDataErrors;
            newFormDataErrors.reportTypeIdError.showError = true;
            newFormDataErrors.reportTypeIdError.message = "REQUIRED";
            setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
            setLoading(false);
            return;
        } else {
            let newFormDataErrors = formDataErrors;
            newFormDataErrors.reportTypeIdError.showError = false;
            setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
        }

        // if (formData.placeOfAssessment?.trim() === "") {
        //     let newFormDataErrors = formDataErrors;
        //     newFormDataErrors.placeOfAssessmentError.showError = true;
        //     newFormDataErrors.placeOfAssessmentError.message = "REQUIRED";
        //     setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
        //     setLoading(false);
        //     return;
        // } else {
        //     let newFormDataErrors = formDataErrors;
        //     newFormDataErrors.placeOfAssessmentError.showError = false;
        //     setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
        // }

        if (!(formData.dateOfAssessment instanceof moment)) {
            let newFormDataErrors = formDataErrors;
            newFormDataErrors.dateOfAssessmentError.showError = true;
            newFormDataErrors.dateOfAssessmentError.message = "REQUIRED";
            setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
            setLoading(false);
            return;
        } else {
            let newFormDataErrors = formDataErrors;
            newFormDataErrors.dateOfAssessmentError.showError = false;
            setFormDataErrors((prevDetails) => ({ ...prevDetails, ...newFormDataErrors }));
        }

        setLoading(false);
        handleNext();
    }

    function getClinicTextInput(clinics: IClinic[]) {

        setLoading(true);

        let clinicsArr = [] as ITextInput[];

        clinics.forEach(clinic => {
            let data = {
                label: clinic.clinicName,
                value: clinic.id
            }

            clinicsArr.push(data);
        });

        setClinics(clinicsArr);

        setLoading(false);
    }

    function getReportTypeTextInput(reportTypes: IReportType[]) {

        setLoading(true);

        let reportTypesArr = [] as ITextInput[];

        reportTypes.forEach(reportType => {
            let data = {
                label: `${reportType.reportName} - ${reportType.clinicName}`,
                value: reportType.id
            }

            reportTypesArr.push(data);
        });

        setReportTypes(reportTypesArr);

        setLoading(false);
    }

    async function saveReport() {

        setLoading(true);

        let mediaAdded = await createMediaDocument(formData.mediaId, media);

        if (!mediaAdded) {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
            setLoading(false);
            return;
        }

        const data = {
            ...formData,
            dueDate: formatLocalISO(formData.dueDate),
            dateOfAssessment: formatLocalISO(formData.dateOfAssessment),
        };

        apiService.addReport(data).then((result) => {

            let data = result.data;

            if (!data?.isSuccessful) {
                let newShowAlert = showAlert;
                newShowAlert.isError = true;
                newShowAlert.message = data?.errorMessage;
                newShowAlert.open = true;
                setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
                return;
            }

            let newShowAlert = showAlert;
            newShowAlert.isError = false;
            newShowAlert.message = "Report Added";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));

            handleClose(true);
        }).catch(() => {
            let newShowAlert = showAlert;
            newShowAlert.isError = true;
            newShowAlert.message = "Unexpected Error";
            newShowAlert.open = true;
            setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        }).finally(() => {
            setLoading(false);
        });
    }

    async function removeFile(file: IFile) {

        setLoading(true);
        await removeMedia(file.fileId).then((response: any) => {
            if (response) {
                let _media = media;
                let _filteredArray = _media.filter((_file) => _file.fileId !== file.fileId);
                setMedia(_filteredArray);

                let _addedFilesMedia = addedFiles;
                let _filterAddedArray = _addedFilesMedia.filter((_file) => _file.fileId !== file.fileId);
                setAddedFiles(_filterAddedArray);
            }
            setLoading(false);
        })
    }

    function getHighestPriorityId(priorities: IPriority[]) {
        const highestPriority = priorities.reduce((prev, current) => {
            return (prev.numberOfDaysAdded > current.numberOfDaysAdded) ? prev : current;
        });

        return highestPriority.id;
    }

    function handleMedia(newFile: IFile[]) {
        setMedia((prev) => {
            const filesToAdd = Array.isArray(newFile) ? newFile : [newFile];
            return [...prev, ...filesToAdd];
        });
    }

    return (
        <Modal showAlert={showAlert} closeAlert={closeAlert} editable={false} open={props.open} close={closeModal}
            style={"add-report-card"} >
            <div className="add-report-container">
                <span className='title'>Add Report</span>

                {view === 0
                    ? (<Form patients={patients} formData={formData} setFormData={updateFormData} formDataErrors={formDataErrors} toAddMedia={toMedia}
                        clinics={clinics} reportTypes={reportTypes} loading={loading} priorities={priorities}
                        referrers={referrers} invoiceStatuses={invoiceStatusValues} />)
                    : (<Fragment />)
                }
                {view === 1
                    ? (<AddMedia removeFile={removeFile} save={saveReport} back={handlePrevious} formData={formData}
                        setFormData={updateFormData} loading={loading} setLoading={setLoading} media={media}
                        setShowAlert={setShowAlert} setMedia={handleMedia} />)
                    : (<Fragment />)
                }
            </div>
        </Modal>
    )
}

function Form(props: {
    formData: any; setFormData: CallableFunction; formDataErrors: any; clinics: ITextInput[]; reportTypes: ITextInput[];
    toAddMedia: CallableFunction; loading: boolean; patients: ITextInput[]; priorities: ITextInput[]; referrers: ITextInput[];
    invoiceStatuses: ITextInput[];
}) {
    return (
        <div className="fill-form">
            <div className="header">
                <div className="stepper">
                    <Stepper
                        steps={[
                            { circleStyle: "active", descriptionStyle: "description-active", description: "Fill the form" },
                            { circleStyle: "inactive", descriptionStyle: "description-inactive", description: "Add media files" }
                        ]} />
                </div>
                <div className="form">
                    <div className="row">
                        <div className="input-field">
                            <InputDropDown title='PATIENT NAME*' type='search' typeable
                                defaultValue={props.formData.patientName} values={props.patients}
                                showError={props.formDataErrors?.patientNameError?.showError}
                                onSelect={(val: any, asValue: any) => {
                                    let newFormData = props.formData;
                                    if (asValue) {
                                        newFormData.patientName = val;
                                        newFormData.patientNameId = "";
                                    } else {
                                        newFormData.patientNameId = val[0].value;
                                        newFormData.patientName = val[0].label;
                                    }
                                    props.setFormData(newFormData);
                                }} />
                        </div>
                        <div className="input-field">
                            <InputDropDown title='PRIORITY*' type='dropdown'
                                defaultValue={props.formData.priorityId} values={props.priorities}
                                showError={props.formDataErrors?.priorityError?.showError}
                                onSelect={(value: any) => {
                                    if (value?.length > 0) {
                                        let newFormData = props.formData;
                                        newFormData.priorityId = value[0].value;
                                        props.setFormData(newFormData);
                                    }
                                }} />
                        </div>
                        <div className="input-field">
                            <DateInput label="DUE DATE*" value={props.formData?.dueDate}
                                showError={props.formDataErrors?.dueDateError?.showError}
                                onChange={(value: any) => {
                                    if (value) {
                                        let newFormData = props.formData;
                                        newFormData.dueDate = value;
                                        props.setFormData(newFormData);
                                    }
                                }} />
                        </div>
                        <div className="input-field">
                            <InputDropDown defaultValue={props.formData.clinicId} title='CLINIC' values={props.clinics} type='search'
                                onSelect={(value: any) => {
                                    if (value?.length > 0) {
                                        let newFormData = props.formData;
                                        newFormData.clinicId = value[0].value;
                                        props.setFormData(newFormData);
                                    }
                                }} />
                        </div>
                        <div className="input-field">
                            <InputDropDown title='REPORT TYPE*' values={props.reportTypes} type='search'
                                showError={props.formDataErrors?.reportTypeIdError?.showError}
                                onSelect={(value: any) => {
                                    if (value?.length > 0) {
                                        let newFormData = props.formData;
                                        newFormData.reportTypeId = value[0].value;
                                        props.setFormData(newFormData);
                                    }
                                }} />
                        </div>
                        <div className="input-field">
                            <Input label='PROVIDER NUMBER' value={props.formData.providerNumber} type='text'
                                showError={props.formDataErrors?.providerNumberError?.showError}
                                onChange={(value: any) => {
                                    let newFormData = props.formData;
                                    newFormData.providerNumber = value;
                                    props.setFormData(newFormData);
                                }} />
                        </div>
                        <div className="input-field">
                            <Input label='PLACE OF ASSESSMENT' value={props.formData.placeOfAssessment} type='text'
                                showError={false}
                                onChange={(value: any) => {
                                    let newFormData = props.formData;
                                    newFormData.placeOfAssessment = value;
                                    props.setFormData(newFormData);
                                }} />
                        </div>
                        <div className="input-field">
                            <DateInput label="DATE OF ASSESSMENT*" value={props.formData.dateOfAssessment}
                                showError={props.formDataErrors?.dateOfAssessmentError?.showError}
                                onChange={(value: any) => {
                                    if (value) {
                                        let newFormData = props.formData;
                                        newFormData.dateOfAssessment = value;
                                        props.setFormData(newFormData);
                                    }
                                }} />
                        </div>
                        <div className="input-field">
                            <InputDropDown title='REFERRER' values={props.referrers} type='search'
                                defaultValue={props.formData.referrer} typeable
                                onSelect={(val: any, asValue: any) => {
                                    let newFormData = props.formData;
                                    if (asValue) {
                                        newFormData.referrer = val;
                                        newFormData.referrerId = null;
                                    } else {
                                        newFormData.referrerId = val[0].value;
                                        newFormData.referrer = val[0].label;
                                    }
                                    props.setFormData(newFormData);
                                }} />
                        </div>
                        <div className="input-field">
                            <Input label='REPORT NAME' value={props.formData.reportName} type='text'
                                onChange={(value: any) => {
                                    let newFormData = props.formData;
                                    newFormData.reportName = value;
                                    props.setFormData(newFormData);
                                }} />
                        </div>
                        <div className="input-field">
                            <InputDropDown title='INVOICE' values={props.invoiceStatuses} type='search'
                                defaultValue={props.formData?.invoiceStatus}
                                onSelect={(value: any) => {
                                    if (value?.length > 0) {
                                        let newFormData = props.formData;
                                        newFormData.invoiceStatus = value[0].value;
                                        props.setFormData(newFormData);
                                    }
                                }} />
                        </div>
                        <div className="input-field checkbox">
                            <CheckBox icon={BlueHomeDotDisable} checkedIcon={BlueHomeDot}
                                checked={props.formData.paperworkReceived}
                                onChange={() => {
                                    let newFormData = props.formData;
                                    newFormData.paperworkReceived = !props.formData.paperworkReceived
                                    props.setFormData(newFormData);
                                }} />
                            Paperwork Received
                        </div>
                        <div className="row">
                            <Input label='COMMENTS' value={props.formData.notes} type='textarea' rows={5}
                                onChange={(value: any) => {
                                    let newFormData = props.formData;
                                    newFormData.notes = value;
                                    props.setFormData(newFormData);
                                }} />
                        </div>
                    </div>
                </div>
                <div className="footer-btn">
                    <div className="button">
                        <DocumentButton loading={props.loading} onClick={() => { props.toAddMedia() }} text={"Next"} />
                    </div>
                </div>
            </div >
        </div >
    )
}

function AddMedia(props: {
    save: CallableFunction; back: CallableFunction; formData: any; media: IFile[]; setShowAlert: CallableFunction;
    setFormData: CallableFunction; loading: boolean; setLoading: CallableFunction; setMedia: CallableFunction;
    removeFile: CallableFunction
}) {
    return (
        <div className="add-media">
            <div className="stepper">
                <Stepper steps={[
                    { circleStyle: "inactive", descriptionStyle: "description-active", description: "Fill the form" },
                    { circleStyle: "active", descriptionStyle: "description-inactive", description: "Add media files" }
                ]} />
            </div>
            <FileUploadUI removeFile={props.removeFile} setLoading={props.setLoading} setMedia={props.setMedia}
                media={props.media} setShowAlert={props.setShowAlert} removeMulitpleFiles={() => { }} />
            <div className="footer-btn">
                {props.formData.totalDuration === 0
                    ? (<span className="skip">
                        <CheckBox icon={BlueHomeDotDisable} checkedIcon={BlueHomeDot} checked={props.formData.adHoc}
                            onChange={() => {
                                let newFormData = props.formData;
                                newFormData.adHoc = !props.formData.adHoc;
                                props.setFormData((prevDetails: any) => ({ ...prevDetails, ...newFormData }));
                            }} />
                        Add Documentation
                    </span>)
                    : (<Fragment />)
                }
                <div className='next-button-content'>
                    <span onClick={() => { props.back() }} className="back">Back</span>
                    <div className="button">
                        <DocumentButton loading={props.loading} onClick={() => { props.save() }} text={"Save"} />
                    </div>
                </div>
            </div>
        </div>
    )
}